import { useEffect, useMemo, useState } from 'react';

import ColaboradorFichaje from '@components/Colaboradores/ColaboradorFichajes';
import ColaboradorInfo from '@components/Colaboradores/ColaboradorInfo';
import ColaboradorLegajo from '@components/Colaboradores/ColaboradorLegajo';
import { LiteVersionPopup } from '@components/General/ProhibitedPopups';
import { usePopup } from '@contexts/popupContext';
import { Application } from '@core/Application';
import { CollaboratorsAnalyticsHelper } from '@core/Analytics/CollaboratorsAnalyticsHelper';
import { Organization } from '@models/Organization';
import { CollaboratorCustomFieldsService } from '@services/CollaboratorCustomFieldsService';
import { VerticalContainer } from '@shared/designSystem/Containers';
import { Modal } from '@shared/designSystem/messages/Modal';
import { TabScreenSleek } from '@shared/designSystem/TabScreen';
import { Flap } from '@shared/models/Flap';
import designSystemStyles from '@shared/styles/designSystemStyles';

import ColaboradorAuditoriaScreen from './ColaboradorAuditoriaScreen';
import ColaboradorDedicatedShiftsScreen from './ColaboradorDedicatedShiftsScreen';
import { CollaboratorCatalogRow } from './ColaboradoresScreenViewModel';
import {
    CollaboratorDetailTab,
    useColaboradorDetailModalScreenViewModel,
} from './ColaboradorDetailModalScreenViewModel';

interface ColaboradorDetailModalScreenProps {
    isOpen: boolean;
    org: Organization;
    collaborator: CollaboratorCatalogRow | null;
    onClose: () => void;
    onCollaboratorChange: (collaborator: CollaboratorCatalogRow) => void | Promise<void>;
    readOnly?: boolean;
}

interface DetailTabItem {
    id: CollaboratorDetailTab;
    label: string;
    isLocked?: boolean;
}

type EditableCollaborator = CollaboratorCatalogRow & {
    photo?: string;
};

export default function ColaboradorDetailModalScreen({
    isOpen,
    org,
    collaborator,
    onClose,
    onCollaboratorChange,
    readOnly = false,
}: ColaboradorDetailModalScreenProps) {
    const { showPopup } = usePopup();
    const customFieldsService = useMemo(
        () => new CollaboratorCustomFieldsService(Application.shared().coreDependencies.httpClient),
        [],
    );
    const viewModel = useColaboradorDetailModalScreenViewModel({
        isOpen,
        orgID: org.id,
        customFieldsService,
    });
    const [editableCollaborator, setEditableCollaborator] = useState<EditableCollaborator | null>(null);

    useEffect(() => {
        if (!collaborator) {
            setEditableCollaborator(null);
            return;
        }

        setEditableCollaborator({
            ...collaborator,
            photo: collaborator.photoUrl,
        });
    }, [collaborator]);

    useEffect(() => {
        if (isOpen && collaborator) {
            CollaboratorsAnalyticsHelper.trackDetailShown(collaborator.id);
        }
    }, [isOpen, collaborator]);

    const tabs: DetailTabItem[] = [
        { id: 'perfil', label: 'Perfil' },
        { id: 'fichajes', label: 'Fichajes', isLocked: org.newFlags?.LiteVersion },
        { id: 'legajo', label: 'Legajo', isLocked: org.newFlags?.LiteVersion },
        { id: 'auditoria', label: 'Auditoria', isLocked: org.newFlags?.LiteVersion },
    ];

    if (org.newFlags?.PresenteeismUserDedicatedShiftsV2) {
        tabs.push({
            id: 'turnos-dedicados',
            label: 'Turnos dedicados',
            isLocked: org.newFlags?.LiteVersion,
        });
    }

    const setCollaborator = (nextCollaborator: EditableCollaborator) => {
        setEditableCollaborator(nextCollaborator);
        void Promise.resolve(onCollaboratorChange({
            ...nextCollaborator,
            photoUrl: nextCollaborator.photo ?? nextCollaborator.photoUrl ?? '',
        })).catch((error) => {
            console.error(error);
        });
    };

    const handleLockedClick = () => {
        showPopup(() => <LiteVersionPopup />);
    };

    const contentByTab: Record<CollaboratorDetailTab, JSX.Element | null> = {
        perfil: editableCollaborator ? (
            <ColaboradorInfo
                orgID={org.id}
                colaborador={editableCollaborator}
                setColaborador={setCollaborator}
                startInEditMode={!readOnly}
                customFields={viewModel.activeCustomFields}
                hideTitle={true}
                onSaveSuccess={() => {
                    if (editableCollaborator) {
                        CollaboratorsAnalyticsHelper.trackDetailProfileSaved(editableCollaborator.id);
                    }
                    onClose();
                }}
                hideEditButton={true}
                hideCancelButton={true}
                readOnly={readOnly}
            />
        ) : null,
        fichajes: editableCollaborator ? (
            <ColaboradorFichaje orgID={org.id} colaborador={editableCollaborator} hideTitle={true} />
        ) : null,
        legajo: editableCollaborator ? (
            <ColaboradorLegajo orgID={org.id} colaborador={editableCollaborator} hideTitle={true} readOnly={readOnly} />
        ) : null,
        auditoria: editableCollaborator ? (
            <ColaboradorAuditoriaScreen
                orgID={org.id}
                colaborador={editableCollaborator}
                customFields={viewModel.activeCustomFields}
                hideTitle={true}
            />
        ) : null,
        'turnos-dedicados': editableCollaborator ? (
            <ColaboradorDedicatedShiftsScreen orgID={org.id} colaborador={editableCollaborator} hideTitle={true} readOnly={readOnly} />
        ) : null,
    };

    const flaps: Flap[] = tabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        isLocked: tab.isLocked,
        content: contentByTab[tab.id] ?? <></>,
    }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={'Detalle del colaborador'}
            width="min(1420px, 92vw)"
            height="min(900px, 88vh)"
            maxWidth="92vw"
            maxHeight="92vh"
        >
            <VerticalContainer
                width="100%"
                height="100%"
                margin={designSystemStyles.paddingZero}
                gap={designSystemStyles.gapMd}
            >
                <TabScreenSleek
                    title=""
                    flaps={flaps}
                    clickedScreen={viewModel.selectedTab}
                    setClickedScreen={(id) => {
                        const detailTab = id as CollaboratorDetailTab;
                        viewModel.setSelectedTab(detailTab);
                        if (editableCollaborator) {
                            CollaboratorsAnalyticsHelper.trackDetailTabSelected(editableCollaborator.id, detailTab);
                        }
                    }}
                    onLockedClick={handleLockedClick}
                />
            </VerticalContainer>
        </Modal>
    );
}
